import React from 'react';
import Icon from '../../../components/AppIcon';

const FinancialProjectionsPanel = ({ data, formatKina, onDrillDown }) => {
  const calculateTotals = () => {
    const totalAllocated = data?.reduce((sum, item) => sum + (item?.allocatedBudgetKina || 0), 0);
    const totalSpent = data?.reduce((sum, item) => sum + (item?.spentAmountKina || 0), 0);
    const totalRemaining = data?.reduce((sum, item) => sum + (item?.remainingBalanceKina || 0), 0);
    const avgVariance = data?.length > 0 ? data?.reduce((sum, item) => sum + (item?.variancePercentage || 0), 0) / data?.length : 0;

    return { totalAllocated, totalSpent, totalRemaining, avgVariance };
  };

  const totals = calculateTotals();

  const getVarianceColor = (variance) => {
    if (variance >= 0) return 'text-success';
    if (variance >= -10) return 'text-warning';
    return 'text-error';
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="font-heading font-semibold text-lg text-foreground mb-1">
            Financial Projections & Budget Analysis
          </h3>
          <p className="caption text-sm text-muted-foreground">
            Quarterly budget forecasts and payroll cost trends in PNG Kina
          </p>
        </div>
        <button
          onClick={() => onDrillDown?.('/departmental-budget-management')}
          className="flex items-center gap-2 text-sm text-primary hover:underline"
        >
          <span>View Details</span>
          <Icon name="ArrowRight" size={16} color="var(--color-primary)" />
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <div className="bg-muted rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Target" size={18} color="var(--color-primary)" />
            <span className="text-xs text-muted-foreground">Total Allocated</span>
          </div>
          <p className="text-2xl font-bold text-foreground">{formatKina?.(totals?.totalAllocated)}</p>
        </div>
        <div className="bg-muted rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="TrendingDown" size={18} color="var(--color-warning)" />
            <span className="text-xs text-muted-foreground">Total Spent</span>
          </div>
          <p className="text-2xl font-bold text-foreground">{formatKina?.(totals?.totalSpent)}</p>
        </div>
        <div className="bg-muted rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Wallet" size={18} color="var(--color-success)" />
            <span className="text-xs text-muted-foreground">Remaining</span>
          </div>
          <p className="text-2xl font-bold text-foreground">{formatKina?.(totals?.totalRemaining)}</p>
        </div>
        <div className="bg-muted rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="Activity" size={18} color="var(--color-accent)" />
            <span className="text-xs text-muted-foreground">Avg Variance</span>
          </div>
          <p className={`text-2xl font-bold ${getVarianceColor(totals?.avgVariance)}`}>
            {totals?.avgVariance?.toFixed(1)}%
          </p>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-foreground">Department</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-foreground">Allocated</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-foreground">Spent</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-foreground">Remaining</th>
              <th className="px-4 py-3 text-center text-xs font-medium text-foreground">Variance</th>
            </tr>
          </thead>
          <tbody>
            {data?.slice(0, 5)?.map((item, index) => (
              <tr key={index} className="border-b border-border hover:bg-muted/50 transition-smooth">
                <td className="px-4 py-3 text-sm text-foreground font-medium">{item?.department}</td>
                <td className="px-4 py-3 text-sm text-right text-foreground">{formatKina?.(item?.allocatedBudgetKina)}</td>
                <td className="px-4 py-3 text-sm text-right text-foreground">{formatKina?.(item?.spentAmountKina)}</td>
                <td className="px-4 py-3 text-sm text-right text-foreground">{formatKina?.(item?.remainingBalanceKina)}</td>
                <td className="px-4 py-3 text-center">
                  <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getVarianceColor(item?.variancePercentage)}`}>
                    {item?.variancePercentage >= 0 ? '+' : ''}{item?.variancePercentage?.toFixed(1)}%
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default FinancialProjectionsPanel;